import { useEffect, useRef, useState } from "react";
import * as api from "../api/client";
import { construirPdf } from "../domain/contracts";
import { validarCotizacion } from "../domain/pricing";
import {
  CANALES_ENVIO,
  NOMBRE_CANAL,
  destinoEnvio,
  errorDestino,
  moneda,
  type CanalEnvio,
} from "../domain/model";
import type { Cotizacion, ResultadoCalculo, Vendedor } from "../domain/types";
import { useEditor } from "../state/editor";
import { useGuardarCotizacion } from "../state/useGuardarCotizacion";
import { ErrorAviso, EstadoOperacion } from "./ui";
import AccionesPropuestas from "./AccionesPropuestas";
import Icono from "./Icono";
import { useConfirmar, usePreguntarSitiosEnCero } from "./Dialogos";
export default function AccionesCotizacion({
  vendedor,
  setBusy,
}: {
  vendedor: Vendedor;
  setBusy: (b: boolean) => void;
}) {
  const multiple = useEditor((s) => s.propuestas.length > 1);
  return multiple ? (
    <AccionesPropuestas vendedor={vendedor} setBusy={setBusy} />
  ) : (
    <AccionesIndividuales vendedor={vendedor} setBusy={setBusy} />
  );
}
function AccionesIndividuales({
  vendedor,
  setBusy,
}: {
  vendedor: Vendedor;
  setBusy: (b: boolean) => void;
}) {
  const confirmar = useConfirmar(),
    guardarCotizacion = useGuardarCotizacion(),
    preguntarSitiosEnCero = usePreguntarSitiosEnCero(),
    [status, setStatus] = useState(""),
    [error, setError] = useState<unknown>(),
    [pdf, setPdf] = useState(""),
    [retry, setRetry] = useState(false),
    lock = useRef(false),
    // Canales que ya salieron para la cotización guardada (con "ambos" son
    // dos): "Reintentar" manda solo lo que falta, sin duplicar el otro.
    enviados = useRef(new Map<CanalEnvio, string | undefined>()),
    // La respuesta a "¿incluir tiendas en cero?" del primer intento: el
    // reintento de un canal pendiente manda el mismo documento que el otro.
    sitiosEnCero = useRef<boolean | null>(null);
  const guardado = useRef<{ c: Cotizacion; r: ResultadoCalculo } | null>(null),
    url = useRef("");
  useEffect(
    () => () => {
      if (url.current) URL.revokeObjectURL(url.current);
    },
    [],
  );
  async function entregar(c: Cotizacion, r: ResultadoCalculo) {
    if (c.canal !== "pdf") return enviarPorCanales(c, r);
    const incluirSitiosEnCero = await preguntarSitiosEnCero(c, r);
    setStatus("Generando PDF…");
    const response = await api.request(
      "cotizador-generar-pdf",
      {
        method: "POST",
        body: JSON.stringify(
          construirPdf(c, r, vendedor, { incluirSitiosEnCero }),
        ),
      },
      120000,
    );
    const blob = await response.blob();
    if (!blob.type.includes("pdf"))
      throw new Error("El servicio no devolvió un PDF.");
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = URL.createObjectURL(blob);
    setPdf(url.current);
    setStatus(
      `Cotización guardada · ${c.numeroOficial}. PDF listo · ${moneda(r.total_general_interno)}`,
    );
    setRetry(false);
  }
  // Un envío por canal (n8n recibe siempre uno solo), en orden.
  async function enviarPorCanales(c: Cotizacion, r: ResultadoCalculo) {
    const incluirSitiosEnCero = (sitiosEnCero.current ??=
      await preguntarSitiosEnCero(c, r));
    const canales = CANALES_ENVIO[c.canal];
    for (const canal of canales) {
      if (enviados.current.has(canal)) continue;
      setStatus(`Generando y enviando por ${NOMBRE_CANAL[canal]}…`);
      const response = await api.request(
        "cotizador-generar-pdf",
        {
          method: "POST",
          body: JSON.stringify(
            construirPdf({ ...c, canal }, r, vendedor, {
              incluirSitiosEnCero,
            }),
          ),
        },
        120000,
      );
      const result = await response.json();
      if (result?.error || result?.ok === false)
        throw new Error(
          `${NOMBRE_CANAL[canal]}: ${result.error || "el envío no fue confirmado."}`,
        );
      enviados.current.set(canal, result?.via);
    }
    // Con plantilla, el WhatsApp lo manda el CRM (GHL) unos segundos
    // después: aquí solo consta que quedó pedido, no que llegó.
    const porCrm = enviados.current.get("whatsapp") === "plantilla";
    setStatus(
      canales.length > 1
        ? porCrm
          ? "Cotización guardada. Correo enviado; en unos segundos te llega por WhatsApp para que se la reenvíes al cliente."
          : "Cotización guardada y envío completado por WhatsApp y correo."
        : porCrm
          ? "Cotización guardada. En unos segundos te llega por WhatsApp para que se la reenvíes al cliente."
          : "Cotización guardada y envío completado.",
    );
    setRetry(false);
  }
  async function ejecutar(enviar: boolean, soloEntrega = false) {
    if (lock.current) return;
    const c = structuredClone(useEditor.getState().cotizacion),
      errores = validarCotizacion(c);
    if (!soloEntrega && errores.length) {
      setError(new Error(errores.join(" ")));
      return;
    }
    const faltaDestino = enviar ? errorDestino(c, vendedor) : null;
    if (faltaDestino) {
      setError(new Error(faltaDestino));
      return;
    }
    if (
      enviar &&
      !soloEntrega &&
      !(await confirmar(
        c.canal === "pdf"
          ? {
              titulo: "Confirmar la cotización y generar el PDF",
              confirmar: "Confirmar y generar",
            }
          : {
              titulo: `Enviar por ${NOMBRE_CANAL[c.canal]}`,
              mensaje: `Destino: ${destinoEnvio(c, vendedor)}`,
              confirmar: "Enviar",
            },
      ))
    )
      return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      if (soloEntrega && guardado.current) {
        // Si corrigió el correo después del fallo, el reintento usa el nuevo
        // (el resto de la cotización es la ya guardada).
        await entregar(
          { ...guardado.current.c, correoEnvio: c.correoEnvio },
          guardado.current.r,
        );
        return;
      }
      setRetry(false);
      guardado.current = null;
      enviados.current.clear();
      sitiosEnCero.current = null;
      if (url.current) {
        URL.revokeObjectURL(url.current);
        url.current = "";
        setPdf("");
      }
      const hecho = await guardarCotizacion(vendedor, {
        entregar: enviar,
        onEstado: setStatus,
      });
      if (!hecho) {
        setStatus("Operación cancelada. No se guardó ni envió.");
        return;
      }
      const { r, saved } = hecho;
      const oficial = {
        ...hecho.c,
        registroId: saved.id,
        revision: saved.revision,
        numeroOficial: saved.numero,
        numeroBaseOficial: saved.numeroBase,
      };
      guardado.current = { c: oficial, r };
      setStatus(
        `${saved.nuevaRevision ? `Nueva revisión ${saved.numero} creada (la versión entregada queda intacta)` : `Cotización guardada · ${saved.numero}`} · ${moneda(r.total_general_interno)}`,
      );
      if (enviar) {
        setRetry(true);
        await entregar(oficial, r);
      }
    } catch (e) {
      setError(e);
      const yaSalio = [...enviados.current].map(([k, via]) =>
        via === "plantilla" ? "WhatsApp (pedido al CRM)" : NOMBRE_CANAL[k],
      );
      setStatus(
        guardado.current
          ? `La cotización está guardada. La generación o el envío no se completaron.${yaSalio.length ? ` Ya salió por ${yaSalio.join(" y ")}: "Reintentar" envía solo lo que falta.` : ""}`
          : "No se confirmó el guardado. Tus datos siguen en el formulario.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <section className="actions card">
      <ErrorAviso error={error} />
      <EstadoOperacion texto={status} fallo={!!error} />
      <div className="row acciones-botones">
        <button onClick={() => void ejecutar(false)}>
          <Icono nombre="guardar" /> Guardar cotización
        </button>
        <button className="primary" onClick={() => void ejecutar(true)}>
          <Icono nombre="enviar" />
          Confirmar y entregar
        </button>
        {retry ? (
          <button
            onClick={async () => {
              if (
                guardado.current?.c.canal === "pdf" ||
                (await confirmar({
                  titulo: "Reintentar el envío",
                  mensaje:
                    "El envío anterior podría haberse procesado. Revisa el destino antes de reintentar para evitar duplicados.",
                  confirmar: "Reintentar",
                }))
              )
                void ejecutar(true, true);
            }}
          >
            Reintentar solo PDF / envío
          </button>
        ) : null}
        {pdf ? (
          <a
            className="button"
            href={pdf}
            download={`Cotizacion-${guardado.current?.c.numeroOficial || "DV"}.pdf`}
          >
            <Icono nombre="descargar" /> Descargar PDF
          </a>
        ) : null}
      </div>
    </section>
  );
}
